import type {
  AttendContactAbsenceEntity,
  AttendContactAbsencePropsPub,
  AttendContactAbsencePropsPvt,
} from './entity/attendContactAbsenceEntity';
import type { AttendContactAbsenceIRepository } from './entity/attendContactAbsenceIRepository';
import type {
  AttendContactLateEntity,
  AttendContactLatePropsPub,
  AttendContactLatePropsPvt,
} from './entity/attendContactLateEntity';
import type { AttendContactLateIRepository } from './entity/attendContactLateIRepository';
import type {
  AttendContactLeaveEarlyEntity,
  AttendContactLeaveEarlyPropsPub,
  AttendContactLeaveEarlyPropsPvt,
} from './entity/attendContactLeaveEarlyEntity';
import type { AttendContactLeaveEarlyIRepository } from './entity/attendContactLeaveEarlyIRepository';
import type { WagakoNurseryIApi } from '@/infra/wagakoNurseryIApi';
import type { AttendContactAbsenceJsonPub, AttendContactAbsenceJsonPvt } from '@/types/domain/attendContact/entities/attendContactAbsenceEntity';
import type { AttendContactLateJsonPub, AttendContactLateJsonPvt } from '@/types/domain/attendContact/entities/attendContactLateEntity';
import type { AttendContactLeaveEarlyJsonPub, AttendContactLeaveEarlyJsonPvt } from '@/types/domain/attendContact/entities/attendContactLeaveEarlyEntity';

import { DomainServiceBase } from '@/domain/domainServiceBase';

/**
 * AttendContactDomainService
 */
export class AttendContactDomainService extends DomainServiceBase {
  private wagakoNurseryApi!: WagakoNurseryIApi;

  private absenceRepository!: AttendContactAbsenceIRepository;

  private lateRepository!: AttendContactLateIRepository;

  private leaveEarlyRepository!: AttendContactLeaveEarlyIRepository;

  /**
   * Injection
   * @param wagakoNurseryApi wagakoNurseryApi
   * @param absenceRepository absenceRepository
   * @param lateRepository lateRepository
   * @param leaveEarlyRepository leaveEarlyRepository
   */
  public inject(
    wagakoNurseryApi: WagakoNurseryIApi,
    absenceRepository: AttendContactAbsenceIRepository,
    lateRepository: AttendContactLateIRepository,
    leaveEarlyRepository: AttendContactLeaveEarlyIRepository,
  ) {
    this.wagakoNurseryApi = wagakoNurseryApi;
    this.absenceRepository = absenceRepository;
    this.lateRepository = lateRepository;
    this.leaveEarlyRepository = leaveEarlyRepository;
  }

  /**
   * getAttendContactAbsenceList
   * @param token トークン
   * @returns 結果
   */
  async getAttendContactAbsenceList(token: string | null) {
    const result = await this.absenceRepository.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: AttendContactAbsenceEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * getAttendContactLateList
   * @param token トークン
   * @returns 結果
   */
  async getAttendContactLateList(token: string | null) {
    const result = await this.lateRepository.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: AttendContactLateEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * getAttendContactLeaveEarlyList
   * @param token トークン
   * @returns 結果
   */
  async getAttendContactLeaveEarlyList(token: string | null) {
    const result = await this.leaveEarlyRepository.getList(token);
    const { nextToken } = result;
    const items = result.items.map((v: AttendContactLeaveEarlyEntity) => v.toJson());
    return {
      /** 次のトークン */
      nextToken,
      /** アイテムリスト */
      items,
    };
  }

  /**
   * sendNurseryAttendContactAbsence
   * @param entity 入力
   */
  async sendNurseryAttendContactAbsence(
    entity: AttendContactAbsenceEntity,
  ) {
    await this.wagakoNurseryApi.send<
      AttendContactAbsencePropsPvt,
      AttendContactAbsenceJsonPvt,
      AttendContactAbsencePropsPub,
      AttendContactAbsenceJsonPub
    >(entity);
  }

  /**
   * removeNurseryAttendContactAbsence
   * @param entity 入力
   */
  async removeNurseryAttendContactAbsence(
    entity: AttendContactAbsenceEntity,
  ) {
    await this.wagakoNurseryApi.remove<
      AttendContactAbsencePropsPvt,
      AttendContactAbsenceJsonPvt,
      AttendContactAbsencePropsPub,
      AttendContactAbsenceJsonPub
    >(entity);
  }

  /**
   * sendNurseryAttendContactLate
   * @param entity 入力
   */
  async sendNurseryAttendContactLate(
    entity: AttendContactLateEntity,
  ) {
    await this.wagakoNurseryApi.send<
      AttendContactLatePropsPvt,
      AttendContactLateJsonPvt,
      AttendContactLatePropsPub,
      AttendContactLateJsonPub
    >(entity);
  }

  /**
   * removeNurseryAttendContactLate
   * @param entity 入力
   */
  async removeNurseryAttendContactLate(
    entity: AttendContactLateEntity,
  ) {
    await this.wagakoNurseryApi.remove<
      AttendContactLatePropsPvt,
      AttendContactLateJsonPvt,
      AttendContactLatePropsPub,
      AttendContactLateJsonPub
    >(entity);
  }

  /**
   * sendNurseryAttendContactLeaveEarly
   * @param entity 入力
   */
  async sendNurseryAttendContactLeaveEarly(
    entity: AttendContactLeaveEarlyEntity,
  ) {
    await this.wagakoNurseryApi.send<
      AttendContactLeaveEarlyPropsPvt,
      AttendContactLeaveEarlyJsonPvt,
      AttendContactLeaveEarlyPropsPub,
      AttendContactLeaveEarlyJsonPub
    >(entity);
  }

  /**
   * removeNurseryAttendContactLeaveEarly
   * @param entity 入力
   */
  async removeNurseryAttendContactLeaveEarly(
    entity: AttendContactLeaveEarlyEntity,
  ) {
    await this.wagakoNurseryApi.remove<
      AttendContactLeaveEarlyPropsPvt,
      AttendContactLeaveEarlyJsonPvt,
      AttendContactLeaveEarlyPropsPub,
      AttendContactLeaveEarlyJsonPub
    >(entity);
  }
}
